import { useEffect, useState } from "react";
import { api, ApiError, type ConsolidadoAvaliacoes } from "../api";
import { IconClose } from "../icons";

/** Cor do cartão conforme a média (verde/amarelo/vermelho). */
function tomMedia(m: number | null): string {
  if (m == null) return "";
  return m >= 7 ? "ficha-card-ok" : m >= 5 ? "ficha-card-warn" : "ficha-card-danger";
}

/**
 * Visão gerencial das avaliações de desempenho: nota média geral, status dos
 * links, média por setor e ranking por colaborador. Consome /api/avaliacoes/consolidado.
 */
export default function ConsolidadoAvaliacoesModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<ConsolidadoAvaliacoes | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    api.avaliacaoLinks
      .consolidado()
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setErro(e instanceof ApiError ? e.message : "Erro ao carregar."));
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ficha" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Avaliações — consolidado</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {erro ? (
            <div className="form-error">{erro}</div>
          ) : !data ? (
            <div className="empty">Carregando…</div>
          ) : (
            <>
              <div className="ficha-cards">
                <div className={`ficha-card ${tomMedia(data.geral.media)}`}>
                  <div className="ficha-card-valor">{data.geral.media != null ? data.geral.media.toFixed(1) : "—"}</div>
                  <div className="ficha-card-label">Nota média geral</div>
                </div>
                <div className="ficha-card">
                  <div className="ficha-card-valor">{data.geral.total}</div>
                  <div className="ficha-card-label">Avaliações</div>
                </div>
                <div className="ficha-card ficha-card-ok">
                  <div className="ficha-card-valor">{data.links.respondidos}</div>
                  <div className="ficha-card-label">Links respondidos</div>
                </div>
                <div className={`ficha-card ${data.links.pendentes > 0 ? "ficha-card-warn" : ""}`}>
                  <div className="ficha-card-valor">{data.links.pendentes}</div>
                  <div className="ficha-card-label">Links pendentes</div>
                </div>
              </div>

              <div className="ficha-secao">
                <div className="ficha-secao-titulo">Média por setor</div>
                {data.porSetor.length === 0 ? (
                  <div className="ficha-secao-vazia">Nenhuma avaliação registrada ainda.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Setor</th>
                          <th>Média</th>
                          <th>Avaliações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.porSetor.map((s, i) => (
                          <tr key={i}>
                            <td>{s.setor}</td>
                            <td>{s.media != null ? s.media.toFixed(1) : "—"}</td>
                            <td>{s.qtd}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="ficha-secao">
                <div className="ficha-secao-titulo">Ranking por colaborador</div>
                {data.porColaborador.length === 0 ? (
                  <div className="ficha-secao-vazia">Nenhuma avaliação registrada ainda.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Colaborador</th>
                          <th>Setor</th>
                          <th>Média</th>
                          <th>Avaliações</th>
                          <th>Último período</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.porColaborador.map((c, i) => (
                          <tr key={c.colaborador_id}>
                            <td>{i + 1}</td>
                            <td>{c.nome}</td>
                            <td>{c.setor ?? "—"}</td>
                            <td>{c.media != null ? c.media.toFixed(1) : "—"}</td>
                            <td>{c.qtd}</td>
                            <td>{c.ultimo_periodo ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
